// ============================================================
// eventHumanizer — traduz evento técnico em frase que um humano entende
// ============================================================
// PROBLEMA QUE RESOLVE:
//   O painel mostrava `SUSPICIOUS_PROCESS_DETECTED` + um JSON cru. Pra saber
//   o que aconteceu, o operador tinha que conhecer o catálogo de eventos de
//   cor e ler payload na mão. Bater o olho e entender era impossível.
//
// PRINCÍPIO: a frase descreve O QUE ACONTECEU, com o JOGADOR como sujeito
//   sempre que ele for o autor ("Abriu o Cheat Engine", "Acelerou o jogo em
//   2,0x"). Nada de tradução literal do nome técnico — "Processo suspeito
//   detectado" não diz mais que o original.
//
// ⚠️ O `tom` É A PARTE MAIS ÚTIL, e não é a severidade:
//   Boa parte do volume do painel são eventos OPERACIONAIS — falha de rede ao
//   baixar ruleset, build não registrada, hot-reload. Eles têm severidade alta
//   e o operador lê como "esse jogador fez algo grave", quando não tem NADA a
//   ver com o jogador. Separar isso em `operacional` é o maior ganho de
//   legibilidade do arquivo inteiro.
//
// O código técnico continua visível na UI, em segundo plano — o engenheiro
// ainda precisa dele; ele só deixa de ser a informação principal.
// ============================================================

export type TomEvento =
  | 'critico'      // alteração confirmada do jogo/binário
  | 'forte'        // sem explicação inocente razoável
  | 'contexto'     // pode ter causa legítima; o operador julga
  | 'info'         // informativo sobre o jogador, sem suspeita
  | 'operacional'; // NÃO é sobre o jogador — é o sistema/rede/config

// ⚠️ O QUE CADA TOM PROMETE AO OPERADOR — e por que `critico` é diferente dos
// outros quatro.
//
// `forte`, `contexto`, `info` e `operacional` são juízos sobre QUANTO existe de
// explicação inocente. `critico` não: ele é uma afirmação sobre O QUE O MOTOR
// FAZ. No RiskEvidencePolicy, o tier `Confirmed` é o único que produz
// `kick_eligible` com UM sinal — ou seja, encerra a sessão sozinho. Todo o
// resto precisa de uma segunda família independente.
//
// Daí o INVARIANTE que o teste de contrato trava
// (src/tests/eventHumanizer.contract.test.ts):
//
//     tom 'critico'  ⟺  tier Confirmed no backend
//
// Ele não admite exceção "declarada", ao contrário das outras divergências.
// Mostrar `critico` (rótulo "alteração confirmada") para um evento que o motor
// classifica como Strong faz o operador ler "isto já expulsou / basta isto"
// quando o motor, sozinho, só abriu um FLAG. Foi exatamente esse erro que se
// repetiu três vezes seguidas, e é a classe que o contrato existe para fechar.
//
// Correspondência canônica com os tiers de RiskEvidencePolicy::Classify:
//   Confirmed  → 'critico'
//   Strong     → 'forte'
//   Contextual → 'contexto'
//   Ignore     → 'operacional' (ruído de sistema) ou 'info' (fato sobre o
//                jogador que o motor não pontua, ex.: HWID_LINK sem sanção)

export interface EventoHumanizado {
  titulo: string;        // frase principal, sujeito = jogador quando aplicável
  detalhe?: string;      // dado concreto extraído do payload
  significado?: string;  // uma linha explicando pra quem não é da área
  tom: TomEvento;
}

type Payload = Record<string, unknown> | null | undefined;

/** Lê string do payload; devolve undefined se ausente/vazia. */
function str(p: Payload, ...chaves: string[]): string | undefined {
  if (!p) return undefined;
  for (const k of chaves) {
    const v = p[k];
    if (typeof v === 'string' && v.trim() !== '') return v.trim();
    if (typeof v === 'number') return String(v);
  }
  return undefined;
}

/** Lê booleano do payload (o `str` acima só trata string/número). */
function bool(p: Payload, chave: string): boolean {
  if (!p) return false;
  const v = p[chave];
  return v === true || v === 'true';
}

/** Formata razão de relógio ("2.003" → "2,0x"). */
function razao(v: string | undefined): string | undefined {
  if (!v) return undefined;
  const n = Number(v);
  if (!Number.isFinite(n)) return undefined;
  return `${n.toFixed(1).replace('.', ',')}x`;
}

/** Nome de arquivo a partir de um caminho, sem expor o caminho todo. */
function baseName(v: string | undefined): string | undefined {
  if (!v) return undefined;
  const partes = v.split(/[\\/]/);
  return partes[partes.length - 1] || v;
}

// Chaves de payload que carregam informação útil pra um humano, em ordem de
// prioridade. A lista veio de varrer o que os detectores REALMENTE emitem
// (`grep 'ev.payload\[' client-sdk/src`) — a primeira versão deste arquivo foi
// escrita adivinhando nomes de campo, e por isso mostrava frase genérica
// justamente onde o dado existia.
const CHAVES_UTEIS: Array<[string, string]> = [
  ['process_name', 'programa'],
  ['dll_name', 'DLL'],
  ['driver_name', 'driver'],
  ['parent_path', 'aberto por'],
  ['path', 'arquivo'],
  ['module_path', 'módulo'],
  ['target_module', 'módulo alvo'],
  ['importing_module', 'módulo que importa'],
  ['import_function', 'função'],
  ['target_function', 'função'],
  ['vfunc_name', 'função gráfica'],
  ['signature_id', 'assinatura'],
  ['owner_signer', 'assinado por'],
  ['cert_subject', 'assinado por'],
  ['signer_subject', 'assinado por'],
  ['access_class', 'tipo de acesso'],
  ['parent_pid', 'PID do pai'],
  ['reason', 'motivo'],
];

/**
 * Último recurso: monta um detalhe com os campos concretos do payload.
 *
 * Existe para que NENHUM evento apareça como frase genérica quando o dado
 * está ali. Se a tradução específica não montou detalhe, isto mostra até 3
 * campos úteis em vez de deixar o operador no escuro.
 */
function detalheGenerico(p: Payload, exceto: string[] = []): string | undefined {
  if (!p) return undefined;
  const partes: string[] = [];
  for (const [chave, rotulo] of CHAVES_UTEIS) {
    if (exceto.includes(chave)) continue;
    const v = str(p, chave);
    if (!v) continue;
    const curto = chave.endsWith('path') || chave === 'module_path' ? baseName(v) : v;
    partes.push(`${rotulo}: ${curto}`);
    if (partes.length === 3) break;
  }
  return partes.length ? partes.join(' · ') : undefined;
}

// ============================================================
// Tabela de tradução
// ============================================================
// Cada entrada recebe o payload e devolve a frase. Fica como função (e não
// como string fixa) porque o valor concreto — qual programa, qual driver,
// quantas vezes mais rápido — é justamente o que torna a linha útil.

const TRADUCOES: Record<string, (p: Payload) => EventoHumanizado> = {
  // ---------- Ferramentas abertas pelo jogador ----------
  SUSPICIOUS_PROCESS_DETECTED: (p) => {
    const nome = baseName(str(p, 'process_name'));
    const dll = baseName(str(p, 'dll_name'));
    const metodo = str(p, 'method') ?? '';
    // O `method` diz COMO foi reconhecido, e isso muda a confiança: nome pode
    // ser coincidência, hash não.
    const porHash = metodo.includes('HASH');
    const porPadrao = metodo.includes('PATTERN');
    return {
      titulo: dll
        ? `Programa com a DLL "${dll}" carregada`
        : nome
          ? `Abriu o programa "${nome}"`
          : 'Abriu um programa suspeito',
      detalhe: [
        porHash ? 'reconhecido pelo conteúdo do arquivo (hash)' : undefined,
        porPadrao ? 'nome bate com padrão suspeito, e sem assinatura' : undefined,
        !porHash && !porPadrao && nome ? 'nome está na lista de programas vigiados' : undefined,
        str(p, 'process_hash_sha256') ? `hash ${str(p, 'process_hash_sha256')!.slice(0, 12)}…` : undefined,
      ].filter(Boolean).join(' · ') || undefined,
      significado: porHash
        ? 'O arquivo é byte a byte um programa de trapaça conhecido — renomear não adianta.'
        : 'Programa de trapaça, depuração ou inspeção estava aberto durante o jogo.',
      tom: 'forte',
    };
  },
  PROCESS_SUSPICIOUS: (p) => TRADUCOES.SUSPICIOUS_PROCESS_DETECTED(p),

  SUSPICIOUS_PARENT_PROCESS: (p) => {
    // Os três `method` deste detector descrevem situações MUITO diferentes.
    // Tratá-los com uma frase só era o que deixava o operador sem saber o que
    // aconteceu — e, no caso mais comum, alarmava sem motivo.
    const metodo = str(p, 'method') ?? '';
    const pid = str(p, 'parent_pid');
    const pai = baseName(str(p, 'parent_path'));

    if (metodo === 'PARENT_NOT_WHITELISTED') {
      return {
        titulo: pai
          ? `Jogo foi aberto pelo programa "${pai}"`
          : 'Jogo foi aberto por um programa fora da lista esperada',
        detalhe: pid ? `PID ${pid}` : undefined,
        significado:
          'O normal é o lançador oficial abrir o jogo. Outro programa pode indicar injeção — ou só um atalho diferente.',
        tom: 'contexto',
      };
    }
    if (metodo === 'PID_REUSE_DETECTED') {
      return {
        titulo: 'Programa que abriu o jogo tem identificador reciclado',
        detalhe: pid ? `PID ${pid} · criado depois do próprio jogo` : undefined,
        significado:
          'O "pai" do processo é mais novo que o jogo — sinal de que o original morreu e o número foi reaproveitado. Costuma ser normal.',
        tom: 'contexto',
      };
    }
    // PARENT_DIED — o caso mais comum, e o mais benigno.
    return {
      titulo: 'O programa que abriu o jogo já havia fechado',
      detalhe: pid ? `PID ${pid} · não foi possível identificar o nome` : 'não foi possível identificar o nome',
      significado:
        'Lançador que fecha sozinho depois de abrir o jogo cai aqui — é o comportamento de vários lançadores. O nome não existe porque o programa já não estava rodando.',
      tom: 'contexto',
    };
  },

  // ---------- Depurador ----------
  DEBUGGER_SUSPECTED: (p) => ({
    titulo: 'Anexou um depurador ao jogo',
    detalhe: str(p, 'method'),
    significado:
      'Depurador permite parar o jogo e inspecionar/alterar a memória em tempo real.',
    tom: 'forte',
  }),
  BREAKPOINT_SUSPECTED: (p) => ({
    titulo: 'Colocou ponto de parada no código do jogo',
    detalhe: str(p, 'method'),
    significado:
      'Técnica de depuração usada para interceptar o jogo em pontos específicos.',
    tom: 'forte',
  }),

  // ---------- Acesso externo à memória ----------
  OPEN_HANDLE_SUSPECTED: (p) => {
    const nome = baseName(str(p, 'path'));
    const assinante = str(p, 'owner_signer');
    return {
      titulo: nome
        ? `Programa "${nome}" acessou a memória do jogo`
        : 'Outro programa acessou a memória do jogo',
      detalhe: [
        assinante ? `assinado por ${assinante}` : 'sem assinatura conhecida',
        str(p, 'access_class') ? `acesso: ${str(p, 'access_class')}` : undefined,
      ].filter(Boolean).join(' · '),
      significado:
        'Acesso com poder de escrita ou injeção — leitura simples não gera este alerta.',
      tom: 'forte',
    };
  },

  // ---------- Grampo em funções ----------
  IAT_TAMPER_SUSPECTED: (p) => ({
    titulo: str(p, 'import_function')
      ? `Chamada "${str(p, 'import_function')}" do Windows foi redirecionada`
      : 'Função do Windows foi redirecionada',
    detalhe: [
      str(p, 'target_module') ? `deveria ir pra ${str(p, 'target_module')}` : undefined,
      str(p, 'current_target') ? `foi parar em ${baseName(str(p, 'current_target'))}` : undefined,
      str(p, 'importing_module') ? `no módulo ${str(p, 'importing_module')}` : undefined,
    ].filter(Boolean).join(' · ') || undefined,
    significado:
      'A chamada do jogo ao Windows foi desviada para código de terceiro.',
    tom: 'forte',
  }),
  IAT_INLINE_HOOK_SUSPECTED: (p) => ({
    titulo: str(p, 'import_function')
      ? `Função "${str(p, 'import_function')}" do Windows foi grampeada`
      : `Função do Windows foi grampeada${
          str(p, 'target_module') ? ` em ${str(p, 'target_module')}` : ''
        }`,
    detalhe: [
      str(p, 'pattern') ? `técnica ${str(p, 'pattern')}` : undefined,
      str(p, 'jmp_offset') !== undefined && str(p, 'jmp_offset') !== '0'
        ? `desvio ${str(p, 'jmp_offset')} bytes após o início`
        : undefined,
      str(p, 'jmp_dest_hex') ? `aponta para ${str(p, 'jmp_dest_hex')}` : undefined,
    ].filter(Boolean).join(' · ') || undefined,
    significado:
      'O começo da função foi reescrito para desviar a execução — técnica clássica de cheat.',
    tom: 'forte',
  }),
  PROLOGUE_HOOK_SIGNED_UNKNOWN: (p) => ({
    titulo: 'Função grampeada por programa assinado, mas desconhecido',
    detalhe: baseName(str(p, 'dest_module', 'module')),
    significado:
      'Pode ser antivírus ou overlay legítimo ainda não catalogado. Precisa de olho humano.',
    tom: 'contexto',
  }),

  // ---------- DLL injetada ----------
  SUSPICIOUS_MODULE_LOADED: (p) => {
    // ⚠️ `module` (2026-08-07, Fase 3) — chave LIDA DO BANCO, não adivinhada.
    // O caminho que pegou cheat em campo, `PROCESS_DLL_NAME_MATCH`, emite
    // `{"method":"…","module":"wpespy.dll"}` — sem `path`. Sem esta chave o
    // painel dizia "DLL suspeita carregada no jogo" e engolia o nome do cheat
    // exatamente na detecção que funcionou. `path` continua em primeiro lugar
    // para não alterar o comportamento de quem já manda caminho.
    const nome = baseName(str(p, 'path', 'module', 'module_name', 'dll_name'));
    // `signed` chega como BOOLEANO no payload — comparar com a string 'false'
    // (como a primeira versão fazia) nunca casava.
    const assinado = p ? p['signed'] === true : false;
    const cert = str(p, 'cert_subject');
    return {
      titulo: nome ? `DLL "${nome}" foi carregada no jogo` : 'DLL suspeita carregada no jogo',
      detalhe: [
        assinado ? `assinada por ${cert ?? 'editor não identificado'}` : 'SEM assinatura digital',
        str(p, 'reason'),
      ].filter(Boolean).join(' · '),
      significado: 'Biblioteca de terceiro entrou no processo do jogo.',
      tom: 'forte',
    };
  },
  MODULE_SUSPICIOUS: (p) => TRADUCOES.SUSPICIOUS_MODULE_LOADED(p),
  DLL_UNLOAD_DONE: (p) => ({
    titulo: 'DLL suspeita foi removida do jogo pelo anti-cheat',
    detalhe: baseName(str(p, 'module_name', 'path')),
    significado: 'O anti-cheat conseguiu descarregar a biblioteca.',
    tom: 'info',
  }),
  DLL_UNLOAD_FAILED: (p) => ({
    titulo: 'Anti-cheat tentou remover DLL suspeita e não conseguiu',
    detalhe: baseName(str(p, 'module_name', 'path')),
    tom: 'contexto',
  }),
  DLL_UNLOAD_REJECTED: () => ({
    titulo: 'Remoção de DLL não foi tentada (biblioteca na lista de confiáveis)',
    tom: 'operacional',
  }),

  // ---------- Overlay / gráficos ----------
  D3D9_OVERLAY_LEGIT: (p) => ({
    titulo: 'Sobreposição de tela legítima ativa',
    detalhe: str(p, 'signer_subject', 'module_path'),
    significado: 'Discord, GeForce, OBS e afins. Não é trapaça.',
    tom: 'info',
  }),
  D3D9_VTABLE_HOOK: () => ({
    titulo: 'Desenho da tela do jogo foi interceptado',
    significado: 'Técnica usada por wallhack e ESP para desenhar por cima do jogo.',
    tom: 'forte',
  }),
  D3D9_INLINE_HOOK: (p) => TRADUCOES.D3D9_VTABLE_HOOK(p),
  D3D9_IAT_HOOK: (p) => TRADUCOES.D3D9_VTABLE_HOOK(p),
  D3D9_FOREIGN_HOOK: (p) => ({
    titulo: 'Sobreposição de tela por programa não reconhecido',
    detalhe: str(p, 'signer_subject', 'module_path'),
    significado: 'Pode ser overlay novo ainda não catalogado, ou cheat visual.',
    tom: 'contexto',
  }),
  // ⚠️ Os dois abaixo estavam em `critico` e o motor NUNCA os classificou como
  // `Confirmed`: RiskEvidencePolicy::Classify põe ambos em `Strong`, família
  // "graphics_hook". Não é rebaixamento de política — é o painel deixando de
  // afirmar um poder de expulsão que o motor não tem para eles. O texto não
  // mudou: continua sendo um ato atribuível e sem explicação inocente, que é
  // exatamente o que `forte` significa aqui.
  D3D9_DLL_HIJACK: () => ({
    titulo: 'Arquivo do Windows foi substituído na pasta do jogo',
    significado:
      'Uma DLL falsa foi colocada junto do jogo para ser carregada no lugar da original.',
    tom: 'forte',
  }),
  D3D9_DLL_TEXT_TAMPERED: () => ({
    titulo: 'Biblioteca gráfica do Windows foi alterada na memória',
    tom: 'forte',
  }),

  // ---------- Integridade do jogo ----------
  // Este evento cobria DOIS acontecimentos diferentes com o mesmo rótulo:
  // "o jogador trocou um arquivo do jogo" (adulteração real) e "o arquivo não
  // pôde ser lido" (antivírus segurando o handle, arquivo ausente, OneDrive
  // não materializado, setor com erro). O backend (RiskEvidencePolicy
  // ::Classify) foi corrigido em 2026-08-04 pra separar os dois pelo campo
  // `integrity_status` — e o painel precisa contar a mesma história, senão
  // um evento que o motor já IGNORA continua parecendo acusação crítica aqui.
  //
  // 2026-08-05 — a frota já distribuída não manda `integrity_status`, mas
  // SEMPRE mandou `payload.status` fino (mismatch/missing/io_error). O
  // RiskScoreService passou a ler esse campo (COALESCE com CASE WHEN escopado
  // a este event_type — RiskScoreService.cpp) pra classificar a frota antiga
  // SEM esperar cliente novo. O painel só está alcançando o motor.
  //
  // PRECEDÊNCIA (do mais confiável pro último recurso — mesma ordem do
  // COALESCE do backend). Os dois campos NÃO são redundantes: um evento pode
  // trazer os dois (cliente novo que também ecoa `status`) e nesse caso
  // `integrity_status` sempre vence, porque é escrito pelo próprio
  // verificador de asset no instante em que decidiu o resultado — `status` é
  // só o vocabulário que a frota antiga usava antes de o campo novo existir.
  //   1. `integrity_status` — "mismatch" | "unreadable" (cliente novo).
  //   2. `payload.status`   — "mismatch" | "missing" | "io_error" (frota
  //      antiga), só considerado quando (1) está ausente.
  //   3. Nem um nem outro — aí sim ambíguo de verdade (payload truncado ou
  //      origem desconhecida): não dá pra acusar nem inocentar.
  GAME_FILES_MODIFIED: (p) => {
    const metodo = str(p, 'method') ?? '';
    const integridade = str(p, 'integrity_status');
    const statusFrota = str(p, 'status');
    const arquivo = str(p, 'asset_path', 'path');

    // ⚠️ 2026-08-05 — tom REBAIXADO de `critico` para `forte`, acompanhando a
    // queda do teto no motor. O RiskEvidencePolicy tirou este evento de
    // `Confirmed` e o deixou em `Strong` (nenhum ramo devolve Confirmed):
    // divergência de hash reportada pelo cliente NÃO encerra sessão sozinha
    // mais — exige uma segunda família independente. Motivo escrito lá: três
    // causas legítimas produzem o MESMO mismatch (patcher que já reescreveu,
    // extração parcial de patch e — a pior — baseline do ruleset desatualizada
    // depois de uma atualização oficial, que acusa a FROTA INTEIRA de uma vez).
    //
    // Manter `critico` aqui dizia ao operador "alteração confirmada", no mesmo
    // tom visual de CLIENT_MODIFIED e SELF_BINARY_MODIFIED — que continuam
    // Confirmed e continuam expulsando sozinhos. A diferença é justamente a que
    // importa na hora de decidir, e o operador não tinha como enxergá-la.
    //
    // A DETECÇÃO NÃO FOI PERDIDA: `forte` ("sinal forte") é o tom que este
    // arquivo reserva a "não há explicação inocente razoável, mas sozinho não
    // expulsa — marca para revisão", que é literalmente o que o motor faz com
    // ele hoje (40-48 pontos, capa em 69 = FLAG na fila do operador).
    const adulteracao = (): EventoHumanizado => ({
      titulo: 'Arquivo do jogo foi modificado',
      detalhe: arquivo,
      significado: 'O arquivo no disco não confere com o original.',
      tom: 'forte',
    });

    // Causas corretas (2026-08-05): antivírus segurando o arquivo, arquivo
    // ausente, arquivo do OneDrive ainda não materializado, erro de disco.
    // "Patcher reescrevendo" SAIU desta lista — o backend corrigiu isso na
    // mesma rodada: um arquivo que o patcher reescreveu abre e lê
    // normalmente, só o hash diverge, então cai em adulteração/mismatch, não
    // aqui (ver comentário de RiskEvidencePolicy::Classify).
    const ilegivel = (): EventoHumanizado => ({
      titulo: 'Não foi possível ler um arquivo do jogo para conferir a integridade',
      detalhe: arquivo,
      significado:
        'Antivírus segurando o arquivo, arquivo ausente, arquivo do OneDrive ainda não materializado ou erro de disco — não é indício de trapaça. O anti-cheat já descarta este caso.',
      tom: 'operacional',
    });

    // [0] CALIBRAÇÃO 2026-08-08 — O ARQUIVO **É** UM CHEAT CATALOGADO.
    //
    // Perguntado PRIMEIRO porque é a afirmação mais específica que existe sobre
    // o mesmo arquivo: casou, sha256 a sha256, com uma amostra que nós
    // coletamos. Não é "não confere com o original" (que patcher, patch parcial
    // e baseline velha também produzem) — é "foi trocado por ISTO AQUI".
    //
    // `critico`, e o tom é honesto: no motor este ramo é tier Confirmed e
    // expulsa sozinho (RiskEvidencePolicy, ramo [1.5]). É o ÚNICO ramo deste
    // evento que voltou a ser crítico; os outros continuam `forte`.
    if (
      integridade === 'known_bad' ||
      metodo === 'ASSET_HASH_KNOWN_BAD' ||
      statusFrota === 'known_bad'
    ) {
      const amostra = str(p, 'known_bad_label');
      return {
        titulo: 'Arquivo do jogo foi trocado por um cheat conhecido',
        detalhe: amostra ? `${arquivo ?? 'arquivo'} — amostra: ${amostra}` : arquivo,
        significado:
          'O conteúdo do arquivo é idêntico a uma amostra de cheat catalogada pela equipe. Não é divergência ambígua: atualização oficial, patcher e erro de download não produzem o hash de um cheat conhecido.',
        tom: 'critico',
      };
    }

    // [1] Arquivo lido com sucesso e o hash diverge do original — adulteração.
    if (integridade === 'mismatch') {
      return adulteracao();
    }

    // [1] Não deu pra LER o arquivo pra conferir — o motor já ignora este caso
    // (RiskEvidencePolicy: family "operational_asset_io", tier Ignore).
    if (integridade === 'unreadable' || metodo === 'ASSET_UNREADABLE') {
      return ilegivel();
    }

    // [2] Sem `integrity_status`: só entra aqui a frota antiga. `status`
    // "missing"/"io_error" são o mesmo acontecimento do ponto de vista da
    // causa ("não consegui ler o arquivo") — mesma leitura do caso ilegível.
    if (statusFrota === 'missing' || statusFrota === 'io_error') {
      return ilegivel();
    }
    if (statusFrota === 'mismatch') {
      return adulteracao();
    }

    // [3] Nem `integrity_status` nem `status` reconhecível: agora sim é
    // ambíguo de verdade — payload truncado ou origem desconhecida. Não dá
    // pra acusar nem inocentar o jogador com o dado que chegou.
    return {
      titulo: 'Arquivo do jogo não confere com o esperado — origem não informou o motivo',
      detalhe: arquivo,
      significado:
        'Payload truncado ou de origem desconhecida — não deu pra saber se foi adulteração ou falha ao ler o arquivo. Avalie com o restante do contexto da sessão.',
      tom: 'contexto',
    };
  },
  CLIENT_MODIFIED: () => ({
    titulo: 'Executável do jogo foi modificado',
    significado: 'O programa do jogo em si foi alterado.',
    tom: 'critico',
  }),
  CLIENT_INTEGRITY_FAILED: (p) => {
    const metodo = str(p, 'method') ?? '';
    if (metodo.toLowerCase().includes('private_rwx')) {
      return {
        titulo: 'Código sem origem conhecida encontrado na memória do jogo',
        significado:
          'Área de memória gravável e executável sem arquivo associado — típico de código injetado.',
        tom: 'forte',
      };
    }
    return {
      titulo: 'Verificação de integridade do jogo falhou',
      detalhe: metodo || undefined,
      tom: 'contexto',
    };
  },

  // ---------- Detectores novos (Fase 3) ----------
  SPEEDHACK_SUSPECTED: (p) => {
    const r = razao(str(p, 'qpc_ratio')) ?? razao(str(p, 'tick_ratio'));
    const acelerou = r && Number(String(r).replace(',', '.').replace('x', '')) > 1;
    return {
      titulo: r
        ? `${acelerou ? 'Acelerou' : 'Desacelerou'} o jogo (${r} do tempo real)`
        : 'Alterou a velocidade do jogo',
      detalhe: `medido por ${String(p?.consecutive ?? '')} amostras seguidas`.trim(),
      significado:
        'O relógio que o jogo enxerga foi adulterado — anda mais rápido ou mais devagar que o tempo real.',
      tom: 'forte',
    };
  },
  INPUT_CADENCE_ANOMALY: (p) => ({
    titulo: 'Cliques/teclas com regularidade de robô',
    detalhe: (() => {
      const modal = str(p, 'modal_gap_ms');
      const cv = str(p, 'cv_pct');
      return modal ? `a cada ~${modal} ms, variação de apenas ${cv ?? '?'}%` : undefined;
    })(),
    significado:
      'Mão humana varia sempre; robô repete o mesmo intervalo. Sozinho não prova nada.',
    tom: 'contexto',
  }),
  VIRTUAL_MACHINE_DETECTED: (p) => {
    const metodo = str(p, 'method');
    if (metodo === 'HYPERVISOR_HOST') {
      return {
        titulo: 'Computador comum com virtualização ligada (Docker/WSL2/segurança)',
        significado: 'NÃO é máquina virtual. É Windows normal — situação da maioria hoje.',
        tom: 'info',
      };
    }
    return {
      titulo: `Está jogando dentro de uma máquina virtual${
        str(p, 'vendor') ? ` (${str(p, 'vendor')})` : ''
      }`,
      significado:
        'Máquina virtual é usada para fugir de banimento — mas também por quem joga em Mac ou notebook corporativo.',
      tom: 'contexto',
    };
  },
  SIGNATURE_DETECTED: (p) => ({
    titulo: `Código de cheat conhecido encontrado na memória${
      str(p, 'signature_id') ? ` (${str(p, 'signature_id')})` : ''
    }`,
    detalhe:
      str(p, 'region_type') === 'private'
        ? 'em área de memória sem arquivo associado'
        : 'dentro de um programa carregado',
    significado: str(p, 'description') ?? 'A "impressão digital" de um cheat catalogado bateu.',
    tom: str(p, 'region_type') === 'private' ? 'forte' : 'contexto',
  }),
  KERNEL_DRIVER_BLOCKLISTED: (p) => ({
    titulo: `Driver de cheat carregado no Windows${
      str(p, 'driver_name') ? ` ("${str(p, 'driver_name')}")` : ''
    }`,
    significado:
      'Driver dá acesso ao núcleo do sistema — não há motivo legítimo para estar ativo durante o jogo.',
    tom: 'forte',
  }),
  KERNEL_BYOVD_DRIVER_LOADED: (p) => ({
    titulo: `Driver com falha conhecida presente${
      str(p, 'driver_name') ? ` ("${str(p, 'driver_name')}")` : ''
    }`,
    significado:
      'Driver legítimo (ex.: MSI Afterburner) com brecha que cheats exploram. Ter instalado NÃO é trapacear.',
    tom: 'contexto',
  }),

  // ---------- Identidade / evasão de ban ----------
  HWID_LINK_DETECTED: (p) => {
    // ⚠️ O DIVISOR DE ÁGUAS NÃO É A FORÇA DO VÍNCULO — É A SANÇÃO.
    // Duas contas na mesma máquina é o normal absoluto (LAN house, casa com
    // irmãos, PC de família). O backend classifica vínculo com alvo NÃO
    // sancionado como `Ignore`, fora do score, exatamente por isso. Se a tela
    // mostrasse tudo como "sinal forte", o operador leria convivência
    // familiar como evasão de ban — punindo justamente quem não fez nada.
    const sancionado = bool(p, 'linked_sanctioned');
    const forte = str(p, 'link_strength') === 'strong';
    const detalhe = (() => {
      const partes: string[] = [];
      const fw = str(p, 'firmware_matches');
      const ident = str(p, 'identifying_matches');
      if (fw && fw !== '0') partes.push(`${fw} da placa-mãe/firmware`);
      if (ident) partes.push(`${ident} identificadores no total`);
      const ignorados = str(p, 'model_only_matches_ignored');
      if (ignorados && ignorados !== '0') {
        partes.push(`${ignorados} de modelo de peça (não contam)`);
      }
      return partes.length ? partes.join(' · ') : undefined;
    })();

    if (!sancionado) {
      return {
        titulo: 'Compartilha o mesmo computador com outra conta',
        detalhe,
        significado:
          'Sem punição na outra conta. Normal em LAN house, casa com irmãos ou PC de família — não conta como risco.',
        tom: 'info',
      };
    }
    const tipo = str(p, 'linked_sanction_type');
    return {
      titulo: `Mesmo computador de uma conta ${
        tipo === 'ban_perm' ? 'banida permanentemente' : 'punida'
      }`,
      detalhe,
      significado: forte
        ? 'Vínculo forte de hardware com conta punida — é o padrão de quem volta depois de banido.'
        : 'Vínculo fraco: poucos identificadores coincidem. Pode ser máquina revendida ou peça trocada.',
      tom: forte ? 'forte' : 'contexto',
    };
  },
  // 2026-08-08 — DUAS CAUSAS, UM EVENTO. O backend passou a emitir este tipo
  // também quando user_id/account_id trocam SEM respaldo de login verificado
  // (antes isso saía como SESSION_IDENTITY_CHANGED, "rodízio operacional", que
  // não pontuava nada — e era o que apagava o sinal do cheater que rotacionava
  // a identidade a cada heartbeat). `payload.reason` separa as duas, e a
  // tradução tem de separar também: "a máquina mudou" e "a pessoa mudou sem
  // verificação" levam a investigações diferentes.
  SESSION_IDENTITY_MISMATCH: (p) => {
    const motivo = str(p, 'reason');
    if (motivo === 'unverified_identity_rotation') {
      return {
        titulo: 'Trocou de jogador no meio da sessão, sem login verificado',
        detalhe: str(p, 'field') === 'account_id' ? 'campo: conta' : 'campo: jogador',
        significado:
          'O cliente do jogo afirmou outra identidade sem que o servidor do jogo ' +
          'tenha confirmado um login. Pode ser integração antiga (LAN house que ' +
          'troca de conta sem reabrir o jogo) — ou um cliente adulterado tentando ' +
          'trocar de dono. Não expulsa sozinho; serve para investigar.',
        tom: 'contexto',
      };
    }
    return {
      titulo: 'Identidade da máquina mudou no meio da sessão',
      significado: 'O jogo informou um identificador diferente do que abriu a sessão.',
      tom: 'forte',
    };
  },
  // FASE 2 (2026-08-07) — sessão velha SEM nenhuma identidade de jogador.
  //
  // 🔴 TOM `operacional`, NÃO `contexto`. Sem esta tradução o tipo cairia no
  // fallback de "tipo desconhecido", que devolve `contexto` — a escala de
  // ACUSAÇÃO. E aqui não existe acusação: quem deveria ter chamado
  // `ORACULO_SetUserId`/`ORACULO_SetAccountId` é o cliente do JOGO, no login. O
  // jogador não escolhe isso e não tem como corrigir. Ler isto como "contexto
  // sobre o jogador" é exatamente o erro de leitura que este evento não pode
  // induzir — o backend já garante o mesmo do lado dele (tier `Ignore`).
  SESSION_IDENTITY_MISSING: (p) => {
    const idadeStr = str(p, 'session_age_seconds');
    const idade = idadeStr !== undefined ? Number(idadeStr) : NaN;
    return {
      titulo: 'Sessão sem login identificado — não dá para sancionar',
      detalhe: Number.isFinite(idade)
        ? `${Math.round(idade / 60)} min de sessão sem conta nem jogador informado.`
        : undefined,
      significado:
        'O cliente do jogo não informou quem está jogando. A detecção continua ' +
        'funcionando, mas qualquer punição fica sem alvo. É pendência de ' +
        'integração do jogo, não conduta do jogador.',
      tom: 'operacional',
    };
  },

  // ---------- Presença / sessão ----------
  SESSION_SILENT: () => ({
    titulo: 'Jogo parou de responder ao anti-cheat',
    significado:
      'Pode ser queda de internet ou jogo fechado — mas também é o que acontece se o anti-cheat for desativado.',
    tom: 'contexto',
  }),
  EMULATION_PATTERN_SUSPECTED: () => ({
    titulo: 'Ritmo de comunicação com o servidor parece automatizado',
    significado: 'Cadência regular demais para um jogo normal.',
    tom: 'contexto',
  }),
  CLIENT_STARTED: () => ({
    titulo: 'Jogador entrou (anti-cheat iniciado)',
    tom: 'info',
  }),

  // ---------- Integridade do próprio anti-cheat ----------
  SELF_BINARY_MODIFIED: () => ({
    titulo: 'Arquivo do anti-cheat foi modificado',
    significado: 'Tentativa de desativar ou adulterar a proteção.',
    tom: 'critico',
  }),
  SELF_BINARY_FILE_HANDLE_OPEN_BY_EXTERNAL: (p) => ({
    titulo: `Programa abriu o arquivo do anti-cheat${
      baseName(str(p, 'process_name')) ? ` ("${baseName(str(p, 'process_name'))}")` : ''
    }`,
    significado: 'Alguém inspecionando ou tentando alterar a proteção.',
    tom: 'contexto',
  }),
  ANTIHACK_INTEGRITY_FAILED: () => ({
    titulo: 'Anti-cheat em memória não confere com a versão oficial',
    tom: 'forte',
  }),

  // ---------- Operacionais (NÃO são sobre o jogador) ----------
  // Este evento cobria DOIS acontecimentos diferentes com o mesmo rótulo
  // operacional cinza: "a equipe não cadastrou a build" (não é sobre o
  // jogador) e "o hash do binário diverge do registrado" (evidência de
  // adulteração). RiskEvidencePolicy::Classify (backend/src/services/
  // RiskEvidencePolicy.cpp) sempre os separou pelo qualificador
  // `integrity_status`: `build_invalid` → Ignore; `integrity_failed` → Strong.
  // O painel dizia "não é acusação ao jogador" nos dois — apagando a
  // detecção no último metro. 2026-08-05: separado para contar a mesma
  // história que o motor já pratica.
  //
  // PRECEDÊNCIA — o único campo que o backend lê para este evento é
  // `payload.integrity_status` (HeartbeatService.cc, RunIntegrityCheck do
  // SDK). Não há campo legado equivalente ao `status` de GAME_FILES_MODIFIED
  // aqui — este evento só existe desde que o campo novo existe.
  //   1. `integrity_status === 'build_invalid'`    → operacional, Ignore.
  //   2. `integrity_status === 'integrity_failed'` → forte, Strong.
  //   3. Qualquer outro valor, incluindo `'unknown'` e ausente — o backend
  //      não descarta (ramo default = Contextual, "build_attestation"; entra
  //      no score). O painel não tem como saber qual dos dois casos é, então
  //      não inventa certeza para nenhum lado.
  BUILD_ATTESTATION_FAILED: (p) => {
    const status = str(p, 'integrity_status');
    const build = str(p, 'client_build');
    const sufixoBuild = build ? ` (build "${build}")` : '';

    // [1] Build do cliente não está no catálogo, ou está desatualizada. O
    // motor ignora: é pendência de cadastro da equipe, não do jogador.
    if (status === 'build_invalid') {
      return {
        titulo: 'Versão do jogo não está cadastrada no sistema',
        significado:
          'Normalmente é build nova ainda não cadastrada pela equipe — não é acusação ao jogador.',
        tom: 'operacional',
      };
    }

    // [2] Hash do binário do cliente diverge do que está registrado. O
    // motor classifica como sinal forte: não encerra a sessão sozinho, mas
    // marca para revisão do operador. Causas legítimas conhecidas existem
    // (atualização em andamento, cliente rodando build ainda não publicada) —
    // por isso o texto não afirma trapaça, só o fato observável.
    if (status === 'integrity_failed') {
      return {
        titulo: `Binário do jogo não confere com o registrado${sufixoBuild}`,
        significado:
          'O hash do executável que o jogador está rodando diverge do hash oficial cadastrado para essa build. Pode ser atualização em andamento ou cliente rodando uma build ainda não publicada — mas também é o que aparece quando o binário foi alterado. Sem explicação inocente óbvia; avalie com o restante do contexto da sessão antes de decidir.',
        tom: 'forte',
      };
    }

    // [3] Nem um nem outro — inclui `'unknown'` (o SDK emite esse valor
    // quando a própria checagem de integridade deu resultado anômalo) e
    // payload sem o campo. O motor não descarta este caso (Contextual, soma
    // ao score) — o painel também não trata como puramente operacional.
    return {
      titulo: `Versão do jogo não pôde ser confirmada${sufixoBuild}`,
      significado:
        'A checagem de integridade da build não retornou um resultado reconhecido — não deu pra confirmar se é pendência de cadastro ou divergência real. Avalie com o restante do contexto da sessão.',
      tom: 'contexto',
    };
  },
  CLIENT_BUILD_INVALID: () => ({
    titulo: 'Versão do jogo não está cadastrada no sistema',
    significado: 'Pendência de cadastro da equipe.',
    tom: 'operacional',
  }),
  SCHEMA_VERSION_DEPRECATED: () => ({
    titulo: 'Jogador está com versão antiga do anti-cheat',
    tom: 'operacional',
  }),
  CLIENT_INTEGRITY_NETWORK_ERROR: () => ({
    titulo: 'Falha de rede ao verificar integridade',
    tom: 'operacional',
  }),
  CLIENT_INTEGRITY_PARSE_ERROR: () => ({
    titulo: 'Resposta inválida na verificação de integridade',
    tom: 'operacional',
  }),
  RULESET_RULE_SKIPPED: (p) => ({
    titulo: 'Uma regra de detecção foi ignorada',
    detalhe: str(p, 'rule_type', 'reason'),
    significado: 'Cobertura reduzida — vale conferir a configuração.',
    tom: 'operacional',
  }),
  RULESET_LOAD_FAILED: () => ({
    titulo: 'Anti-cheat não conseguiu carregar as regras do servidor',
    significado: 'Ficou com o conjunto reduzido de detectores. Problema de configuração/rede.',
    tom: 'operacional',
  }),
  RULESET_SIG_INVALID: () => ({
    titulo: 'Assinatura das regras inválida',
    significado: 'Configuração errada ou tentativa de adulterar as regras.',
    tom: 'operacional',
  }),
  RULESET_CACHE_ROLLBACK_REJECTED: () => ({
    titulo: 'Tentativa de voltar para regras antigas foi bloqueada',
    tom: 'operacional',
  }),
  RULESET_HOT_RELOADED: () => ({
    titulo: 'Regras atualizadas sem reiniciar o jogo',
    tom: 'operacional',
  }),
  RULESET_HOT_RELOAD_FAILED: () => ({
    titulo: 'Falha ao atualizar as regras',
    tom: 'operacional',
  }),
  SDK_UPDATE_TRIGGERED: () => ({
    titulo: 'Atualização do anti-cheat iniciada',
    tom: 'operacional',
  }),
  UPDATE_BINARY_INVALID: () => ({
    titulo: 'Arquivo de atualização inválido',
    tom: 'operacional',
  }),
  UPDATE_SIG_INVALID: () => ({
    titulo: 'Assinatura da atualização inválida',
    tom: 'operacional',
  }),
  UPDATE_DOWNLOAD_FAILED: () => ({
    titulo: 'Falha ao baixar a atualização',
    tom: 'operacional',
  }),
  UPDATE_INSTALL_FAILED: () => ({
    titulo: 'Falha ao instalar a atualização',
    tom: 'operacional',
  }),
  SANCTION_AUTO_ESCALATED: (p) => ({
    titulo: 'Punição aplicada automaticamente por reincidência',
    detalhe: str(p, 'sanction_type', 'target_kind'),
    significado: 'O jogador já havia sido expulso antes; o sistema escalou a punição.',
    tom: 'info',
  }),
};

/**
 * Todos os tipos de evento que este arquivo humaniza.
 *
 * Existe para o teste de contrato (src/tests/eventHumanizer.contract.test.ts)
 * conseguir provar EXAUSTIVIDADE: adicionar uma tradução nova sem declarar o
 * tom esperado — e o tier que o backend pratica — quebra o teste. Sem isto, o
 * contrato só cobriria o que alguém lembrou de listar, que é a forma como a
 * divergência painel×motor voltou três vezes.
 */
export const TIPOS_TRADUZIDOS: readonly string[] = Object.freeze(
  Object.keys(TRADUCOES).sort(),
);

/**
 * Traduz um evento técnico em frase legível.
 *
 * Tipo desconhecido NÃO quebra a tela: devolve o nome técnico formatado de
 * forma legível e tom `contexto`. Isso importa porque o catálogo cresce a
 * cada sprint — a UI não pode ficar em branco quando um evento novo aparecer
 * antes de alguém escrever a tradução dele.
 */
export function humanizarEvento(
  eventType: string,
  payload?: Payload,
): EventoHumanizado {
  const fn = TRADUCOES[eventType];
  if (fn) {
    try {
      const h = fn(payload);
      // Tradução que não montou detalhe ainda pode mostrar dado concreto —
      // frase genérica com o payload cheio de informação era exatamente a
      // reclamação: "continuo sem saber o que aconteceu".
      if (!h.detalhe) {
        const generico = detalheGenerico(payload);
        if (generico) return { ...h, detalhe: generico };
      }
      return h;
    } catch {
      // Payload em formato inesperado nunca pode derrubar a listagem.
    }
  }
  return {
    titulo: eventType
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/^./, (c) => c.toUpperCase()),
    detalhe: detalheGenerico(payload),
    tom: 'contexto',
  };
}

/** Classes Tailwind por tom — usadas nas listagens de evento e incidente. */
export function corDoTom(tom: TomEvento): string {
  switch (tom) {
    case 'critico':
      return 'text-red-300 border-red-500/40 bg-red-500/10';
    case 'forte':
      return 'text-orange-300 border-orange-500/40 bg-orange-500/10';
    case 'contexto':
      return 'text-yellow-200 border-yellow-500/30 bg-yellow-500/10';
    case 'info':
      return 'text-sky-300 border-sky-500/30 bg-sky-500/10';
    case 'operacional':
      return 'text-gray-400 border-gray-600/40 bg-gray-600/10';
  }
}

/** Rótulo curto do tom, para o badge. */
export function rotuloDoTom(tom: TomEvento): string {
  switch (tom) {
    case 'critico':
      return 'alteração confirmada';
    case 'forte':
      return 'sinal forte';
    case 'contexto':
      return 'precisa de contexto';
    case 'info':
      return 'informativo';
    case 'operacional':
      return 'não é sobre o jogador';
  }
}

// ============================================================
// Resumo de INCIDENTE (agrupamento de eventos)
// ============================================================
// Um incidente é um punhado de eventos agregados. O painel mostrava só
// "Recon · 55 suspect · 6 events / 2 occurrences" — que não diz NADA sobre o
// que o jogador fez. E na tela de detalhe, 6 eventos iguais viravam 6 linhas
// idênticas, empurrando qualquer coisa diferente para fora da vista.
//
// Aqui os eventos são agrupados por tipo, ordenados por gravidade (e não por
// ordem de chegada), e o mais grave vira o título do incidente.

export interface LinhaResumo {
  vezes: number;
  titulo: string;
  tom: TomEvento;
}

export interface ResumoIncidente {
  titulo: string;       // o que aconteceu de mais grave
  linhas: LinhaResumo[];// tudo que compõe, agrupado e ordenado
  tom: TomEvento;       // pior tom do conjunto
  totalAlertas: number;
  /** true = o incidente inteiro é ruído operacional, não conduta do jogador. */
  soOperacional: boolean;
}

const PESO_TOM: Record<TomEvento, number> = {
  critico: 5,
  forte: 4,
  contexto: 3,
  info: 2,
  operacional: 1,
};

export interface EventoParaResumo {
  event_type: string;
  payload?: Record<string, unknown> | null;
  occurrence_count?: number;
}

/**
 * Agrupa os eventos de evidência de um incidente numa leitura de relance.
 *
 * Ordena por GRAVIDADE, não por volume: 1 driver de cheat carregado importa
 * mais que 40 varreduras de handle. Ordenar por contagem enterraria o sinal
 * forte embaixo do ruído — que é exatamente o problema que este resumo existe
 * para resolver.
 */
export function resumirIncidente(eventos: EventoParaResumo[]): ResumoIncidente {
  if (!eventos || eventos.length === 0) {
    return {
      titulo: 'Sem eventos de evidência registrados',
      linhas: [],
      tom: 'operacional',
      totalAlertas: 0,
      soOperacional: true,
    };
  }

  const porTipo = new Map<string, LinhaResumo>();
  let total = 0;

  for (const ev of eventos) {
    const h = humanizarEvento(ev.event_type, ev.payload);
    const vezes = Math.max(1, ev.occurrence_count ?? 1);
    total += vezes;
    const atual = porTipo.get(h.titulo);
    if (atual) {
      atual.vezes += vezes;
    } else {
      porTipo.set(h.titulo, { vezes, titulo: h.titulo, tom: h.tom });
    }
  }

  const linhas = [...porTipo.values()].sort(
    (a, b) => PESO_TOM[b.tom] - PESO_TOM[a.tom] || b.vezes - a.vezes,
  );

  const soOperacional = linhas.every((l) => l.tom === 'operacional');
  return {
    titulo: linhas[0].titulo,
    linhas,
    tom: linhas[0].tom,
    totalAlertas: total,
    soOperacional,
  };
}

/** "6 alertas · 2 repetições" — contagens em português, sem jargão. */
export function contagemLegivel(eventCount: number, occurrenceSum: number): string {
  const alertas = `${eventCount} alerta${eventCount === 1 ? '' : 's'}`;
  if (!occurrenceSum || occurrenceSum <= eventCount) return alertas;
  return `${alertas} · ${occurrenceSum} repetições`;
}
